import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import type { Session } from "@supabase/supabase-js";
import type { Tables } from "@/integrations/supabase/types";

type Member = Tables<"members">;

interface AuthState {
  session: Session | null;
  member: Member | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshMember: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  session: null,
  member: null,
  loading: true,
  signOut: async () => {},
  refreshMember: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  // Guard against stale member fetches overwriting newer state
  const fetchIdRef = useRef(0);

  const fetchMember = async (authId: string, fetchId: number) => {
    try {
      const { data, error } = await supabase
        .from("members")
        .select("*")
        .eq("auth_id", authId)
        .eq("status", "active")
        .maybeSingle();

      // Only apply if this is still the latest fetch
      if (fetchIdRef.current !== fetchId) return;

      if (error) {
        console.error("Failed to fetch member:", error.message);
        Sentry.captureException(error, { tags: { context: "auth_fetch_member" } });
        return;
      }
      setMember(data);
    } catch (err) {
      console.error("Failed to fetch member:", err);
      Sentry.captureException(
        err instanceof Error ? err : new Error(String(err)),
        { tags: { context: "auth_fetch_member" } },
      );
    }
  };

  const refreshMember = async () => {
    if (session?.user?.id) {
      const id = ++fetchIdRef.current;
      await fetchMember(session.user.id, id);
    }
  };

  useEffect(() => {
    // 1. Set up listener FIRST (before getSession) so we don't miss events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        // CRITICAL: Do NOT await anything here — fire-and-forget only
        // Awaiting Supabase calls inside this callback causes a deadlock
        setSession(newSession);

        if (newSession?.user?.id) {
          const id = ++fetchIdRef.current;
          // Use setTimeout to ensure this runs outside the auth lock
          setTimeout(() => {
            fetchMember(newSession.user.id, id).then(() => {
              setLoading(false);
            });
          }, 0);
        } else {
          fetchIdRef.current++;
          setMember(null);
          setLoading(false);
        }
      }
    );

    // 2. Restore session — this will trigger onAuthStateChange with INITIAL_SESSION
    supabase.auth.getSession().then(({ data: { session: restoredSession } }) => {
      // If no session, onAuthStateChange may not fire with a useful event
      if (!restoredSession) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setMember(null);
  };

  return (
    <AuthContext.Provider value={{ session, member, loading, signOut, refreshMember }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
